import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Phone, AlertTriangle, Sparkles, TrendingUp, Check, X } from 'lucide-react';

function MobileScore() {
  const [mobile, setMobile] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get('/api/profile');
      if (response.data.exists) {
        setProfile(response.data.profile);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleAnalyze = async () => {
    if (!mobile) return;

    setLoading(true);
    try {
      const response = await axios.post('/api/numerology/mobile-detailed', { mobile });
      setResult(response.data);
    } catch (err) {
      console.error('Error:', err);
      alert(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#10B981';
    if (score >= 60) return '#F59E0B';
    return '#DC2626';
  };

  const getCategoryColor = (category) => {
    if (category.includes('STRONG')) return '#10B981';
    if (category.includes('BASIC')) return '#F59E0B';
    return '#DC2626';
  };

  return (
    <div className="page">
      <div className="container" style={{ maxWidth: '900px' }}>
        <button onClick={() => navigate('/dashboard')} className="btn btn-ghost" style={{ marginBottom: '2rem' }}>
          <ArrowLeft size={20} style={{ marginRight: '0.5rem' }} />
          Dashboard
        </button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>Lucky Mobile Number Analysis</h1>
          <p style={{ color: '#9CA3AF', marginBottom: '2rem' }}>Comprehensive analysis using your birth details and numerology profile</p>
        </motion.div>

        {!profile && (
          <div className="alert alert-warning" style={{ marginBottom: '2rem' }}>
            Please complete your profile first. This analysis requires your birth date and name.
            <button onClick={() => navigate('/data-input')} className="btn btn-primary" style={{ marginTop: '1rem' }}>
              Complete Profile
            </button>
          </div>
        )}

        {profile && (
          <div className="card" style={{ marginBottom: '2rem', background: '#1E2139', padding: '1rem' }}>
            <p style={{ fontSize: '0.875rem', color: '#9CA3AF' }}>
              Analysis will use: Driver ({profile.birth_date}) | Name ({profile.full_name})
            </p>
          </div>
        )}

        <div className="card" style={{ marginBottom: '2rem' }}>
          <div className="form-group">
            <label className="form-label">Enter Mobile Number</label>
            <input
              type="text"
              className="form-input"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="9876543210 or +91-9876543210"
              data-testid="mobile-input"
            />
          </div>

          <button
            onClick={handleAnalyze}
            className="btn btn-primary"
            disabled={loading || !mobile || !profile}
            data-testid="analyze-button"
            style={{ width: '100%' }}
          >
            {loading ? 'Analyzing...' : 'Analyze Mobile Number'}
          </button>
        </div>

        {result && !result.error && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* Score Card */}
            <div className="card" style={{ 
              textAlign: 'center', 
              marginBottom: '2rem',
              background: `linear-gradient(135deg, ${getCategoryColor(result.category)}15 0%, ${getCategoryColor(result.category)}05 100%)`,
              border: `3px solid ${getCategoryColor(result.category)}`
            }}>
              <div style={{
                width: '150px',
                height: '150px',
                margin: '0 auto 1.5rem',
                borderRadius: '50%',
                border: `8px solid ${getScoreColor(result.score)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `${getScoreColor(result.score)}10`
              }}>
                <div>
                  <div style={{ fontSize: '3rem', fontWeight: 700, color: getScoreColor(result.score) }}>
                    {result.score}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#9CA3AF' }}>/ 100</div>
                </div>
              </div>

              <h2 style={{ fontSize: '2rem', marginBottom: '1rem', color: getCategoryColor(result.category) }}>
                {result.category}
              </h2>

              <div style={{ display: 'inline-block', padding: '1rem 2rem', background: '#1E2139', borderRadius: '12px', marginBottom: '1rem' }}>
                <p style={{ fontSize: '0.875rem', color: '#9CA3AF', marginBottom: '0.5rem' }}>Mobile Vibration</p>
                <p style={{ fontSize: '2rem', fontWeight: 700, color: '#00D9FF' }}>{result.mobile_vibration}</p>
              </div>

              <div style={{ padding: '1.5rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '12px', textAlign: 'left' }}>
                <h3 style={{ fontSize: '1.125rem', marginBottom: '0.75rem', color: '#3B82F6' }}>Best Use:</h3>
                <p style={{ fontSize: '1rem', lineHeight: 1.7 }}>{result.best_use}</p>
              </div>
            </div>

            {/* Why Lucky or Not */}
            <div className="card" style={{ marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Sparkles size={24} color="#00D9FF" />
                Why This Number is {result.category.split(' ')[0]}
              </h3>
              <p style={{ fontSize: '1.0625rem', lineHeight: 1.8, color: '#E5E7EB' }}>
                {result.why_lucky_or_not}
              </p>
            </div>

            {/* Detailed Explanation */}
            {result.detailed_explanation && (
              <div className="card" style={{ marginBottom: '2rem', background: 'linear-gradient(135deg, rgba(0, 217, 255, 0.08) 0%, rgba(0, 217, 255, 0.03) 100%)' }}>
                <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Detailed Analysis</h3>
                <div style={{ fontSize: '1rem', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                  {result.detailed_explanation}
                </div>
              </div>
            )}

            {/* Behavioral Impact */}
            <div className="card" style={{ marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#7C3AED' }}>Behavioral Impact</h3>
              <p style={{ fontSize: '1.0625rem', lineHeight: 1.8 }}>
                {result.behavioral_impact}
              </p>
            </div>

            {/* Warnings */}
            {result.warnings && result.warnings.length > 0 && (
              <div className="card" style={{ 
                marginBottom: '2rem',
                border: '3px solid #DC2626',
                background: 'rgba(220, 38, 38, 0.08)'
              }}>
                <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: '#DC2626', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <AlertTriangle size={24} />
                  Warnings & Cautions
                </h3>
                {result.warnings.map((warning, i) => (
                  <div key={i} style={{ 
                    padding: '1.5rem', 
                    background: 'rgba(220, 38, 38, 0.1)', 
                    borderRadius: '12px',
                    marginBottom: '1rem',
                    borderLeft: '4px solid #DC2626'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                      <h4 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#DC2626' }}>
                        {warning.type}
                      </h4>
                      <span className="badge" style={{ background: 'rgba(220, 38, 38, 0.2)', color: '#DC2626', textTransform: 'uppercase' }}>
                        {warning.severity}
                      </span>
                    </div>
                    <p style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>
                      <strong>Issue:</strong> {warning.description}
                    </p>
                    <p style={{ fontSize: '1rem', marginBottom: '0.75rem', color: '#F9FAFB' }}>
                      <strong>Impact:</strong> {warning.impact}
                    </p>
                    <div style={{ padding: '1rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '8px' }}>
                      <p style={{ fontSize: '0.9375rem', color: '#3B82F6' }}>
                        <strong>Recommendation:</strong> {warning.recommendation}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Positive Aspects */}
            {result.positive_aspects && result.positive_aspects.length > 0 && (
              <div className="card" style={{ marginBottom: '2rem', background: 'rgba(16, 185, 129, 0.08)' }}>
                <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: '#10B981', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <TrendingUp size={24} />
                  Positive Energy Combinations
                </h3>
                {result.positive_aspects.map((positive, i) => (
                  <div key={i} style={{ 
                    padding: '1.25rem', 
                    background: 'rgba(16, 185, 129, 0.1)', 
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    borderLeft: '4px solid #10B981'
                  }}>
                    <h4 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem', color: '#10B981' }}>
                      {positive.combination}
                    </h4>
                    <p style={{ fontSize: '1rem', lineHeight: 1.7 }}>
                      {positive.benefit}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Reference Info */}
            <div className="card" style={{ background: '#1E2139' }}>
              <h4 style={{ fontSize: '1.125rem', marginBottom: '1rem', color: '#9CA3AF' }}>Your Core Numbers (for reference)</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#00D9FF' }}>{result.driver}</div>
                  <p style={{ fontSize: '0.875rem', color: '#9CA3AF' }}>Driver</p>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#7C3AED' }}>{result.conductor}</div>
                  <p style={{ fontSize: '0.875rem', color: '#9CA3AF' }}>Conductor</p>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#10B981' }}>{result.name_number}</div>
                  <p style={{ fontSize: '0.875rem', color: '#9CA3AF' }}>Name Number</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default MobileScore;
